import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  LogOut,
  Bike,
  MapPin,
  Phone,
  Package,
  Clock,
  Navigation,
  CheckCircle2,
} from "lucide-react";
import { motion } from "framer-motion";
import DeliveryMap from "@/components/DeliveryMap";

export default function DeliveryPanel() {
  // =========================================================
  // DELIVERY LOGIN
  // =========================================================

  const [token, setToken] = useState(
    localStorage.getItem("icp_delivery_token") || ""
  );

  const [password, setPassword] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cateringOrders, setCateringOrders] = useState([]);
  // =========================================================
  // LOAD ORDERS
  // =========================================================

  const refresh = async () => {
    try {
      const [ordersResponse, cateringResponse] =
        await Promise.all([
          api.get("/orders"),
          api.get("/delivery/catering"),
        ]);

      const normalOrders =
        Array.isArray(ordersResponse.data)
          ? ordersResponse.data
          : [];

      const cateringOrders =
        Array.isArray(cateringResponse.data)
          ? cateringResponse.data.map((job) => ({
              ...job,

              // Mark this as catering so the UI
              // can handle it separately.
              is_catering: true,

              // DeliveryPanel expects order_type.
              order_type: "CATERING",

              // Catering customer information
              delivery: {
                name: job.name,
                phone: job.phone,
                email: job.email,
                address: job.location,
                pincode: job.pincode,
                lat: job.latitude,
                lng: job.longitude,
                map_link: job.map_link,
              },

              // Catering does not have normal order items.
              items: [],

              // Use quotation total as the amount.
              total:
                job.quotation?.total_amount ?? 0,
            }))
          : [];

      setOrders([
        ...normalOrders,
        ...cateringOrders,
      ]);
    } catch (error) {
      console.error(
        "Delivery orders loading error:",
        error
      );

      toast.error(
        error?.response?.data?.detail ||
          "Unable to load delivery orders"
      );
    }
  };

  // =========================================================
  // AUTO REFRESH
  // =========================================================

  useEffect(() => {
    if (!token) return;

    refresh();

    const interval = setInterval(() => {
      refresh();
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [token]);

  // =========================================================
  // LOGIN
  // =========================================================

  const login = async () => {
    if (!password.trim()) {
      toast.error("Enter delivery password");
      return;
    }

    setLoading(true);

    try {
      const response = await api.post(
        "/auth/delivery",
        {
          password: password.trim(),
        }
      );

      const deliveryToken =
        response.data?.token;

      if (!deliveryToken) {
        throw new Error(
          "Delivery token missing"
        );
      }

      localStorage.setItem(
        "icp_delivery_token",
        deliveryToken
      );

      setToken(deliveryToken);
      setPassword("");

      toast.success(
        "Welcome to Delivery Panel"
      );
    } catch (error) {
      console.error(
        "Delivery login error:",
        error
      );

      toast.error(
        error?.response?.data?.detail ||
          "Invalid delivery password"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // LOGOUT
  // =========================================================

  const logout = () => {
    localStorage.removeItem(
      "icp_delivery_token"
    );

    setToken("");
    setOrders([]);
  };

  // =========================================================
  // UPDATE STATUS
  // =========================================================
  const setStatus = async (
    orderId,
    status
  ) => {
    try {
      const order = orders.find(
        (item) => item.id === orderId
      );

      if (!order) {
        toast.error("Order not found");
        return;
      }

      if (order.is_catering) {
        await api.patch(
          `/kitchen/catering/${orderId}/status`,
          {
            status,
          }
        );
      } else {
        await api.patch(
          `/orders/${orderId}/status`,
          {
            status,
          }
        );
      }

      toast.success(
        `Order marked as ${status.replaceAll("_", " ")}`
      );

      await refresh();

    } catch (error) {
      console.error(
        "Delivery status error:",
        error
      );

      toast.error(
        error?.response?.data?.detail ||
        "Could not update order"
      );
    }
  };
  // =========================================================
  // START LIVE GPS
  // =========================================================

  const startLiveTracking = (orderId) => {
    if (!navigator.geolocation) {
      toast.error(
        "GPS is not supported on this device"
      );
      return;
    }

    const watchId =
      navigator.geolocation.watchPosition(
        async (position) => {
          const latitude =
            position.coords.latitude;

          const longitude =
            position.coords.longitude;

          try {
            const isCatering =
              orders.find(
                (order) => order.id === orderId
              )?.is_catering;

            if (isCatering) {
              await api.patch(
                `/delivery/catering/${orderId}/location`,
                {
                  lat: latitude,
                  lng: longitude,
                }
              );
            } else {
              await api.patch(
                `/orders/${orderId}/location`,
                {
                  lat: latitude,
                  lng: longitude,
                }
              );
            }

            // Refresh so map gets newest
            // delivery partner location.
            refresh();
          } catch (error) {
            console.error(
              "GPS update error:",
              error
            );
          }
        },
        (error) => {
          console.error(
            "GPS error:",
            error
          );

          if (error.code === 1) {
            toast.error(
              "Please allow GPS permission"
            );
          } else if (error.code === 2) {
            toast.error(
              "Unable to determine your location"
            );
          } else if (error.code === 3) {
            toast.error(
              "GPS request timed out"
            );
          } else {
            toast.error("GPS error");
          }
        },
        {
          enableHighAccuracy: true,
          maximumAge: 5000,
          timeout: 15000,
        }
      );

    localStorage.setItem(
      `icp_delivery_watch_${orderId}`,
      String(watchId)
    );

    toast.success(
      "Live GPS tracking started"
    );
  };

  // =========================================================
  // STOP LIVE GPS
  // =========================================================

  const stopLiveTracking = (orderId) => {
    const storedWatchId =
      localStorage.getItem(
        `icp_delivery_watch_${orderId}`
      );

    if (storedWatchId) {
      navigator.geolocation.clearWatch(
        Number(storedWatchId)
      );

      localStorage.removeItem(
        `icp_delivery_watch_${orderId}`
      );
    }

    toast.success(
      "Live GPS tracking stopped"
    );
  };

  // =========================================================
  // OPEN CUSTOMER LOCATION
  // =========================================================

  const openCustomerLocation = (order) => {
    const delivery =
      order?.delivery || {};

    const mapLink =
      delivery.map_link ||
      order.map_link;

    if (mapLink) {
      window.open(
        mapLink,
        "_blank",
        "noopener,noreferrer"
      );

      return;
    }

    const lat = Number(
      order.delivery_lat ??
        delivery.lat ??
        delivery.latitude
    );

    const lng = Number(
      order.delivery_lng ??
        delivery.lng ??
        delivery.longitude
    );

    if (
      Number.isFinite(lat) &&
      Number.isFinite(lng)
    ) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
        "_blank",
        "noopener,noreferrer"
      );

      return;
    }

    toast.error(
      "Customer location is not available"
    );
  };

  // =========================================================
  // LOGIN SCREEN
  // =========================================================

  if (!token) {
    return (
      <div className="max-w-md mx-auto py-24 px-6">
        <div className="glass rounded-3xl p-8">

          <Bike className="w-10 h-10 text-[#B8E14A] mb-3" />

          <h1 className="font-heading text-4xl mb-2">
            Delivery Login
          </h1>

          <p className="text-sm text-[#B7D8D5] mb-6">
            Enter delivery partner password
          </p>

          <input
            type="password"
            data-testid="delivery-password"
            value={password}
            onChange={(event) =>
              setPassword(
                event.target.value
              )
            }
            onKeyDown={(event) => {
              if (
                event.key === "Enter"
              ) {
                login();
              }
            }}
            placeholder="Delivery password"
            className="w-full rounded-full border border-[rgba(184,225,74,0.35)] bg-white text-[#1F5F5C] px-6 py-3 mb-4 outline-none focus:ring-2 focus:ring-[#B8E14A]"
          />

          <button
            data-testid="delivery-login"
            onClick={login}
            disabled={loading}
            className="btn-primary w-full justify-center disabled:opacity-50"
          >
            {loading
              ? "Logging in..."
              : "Login"}
          </button>

        </div>
      </div>
    );
  }

  // =========================================================
  // IMPORTANT DELIVERY FILTER
  //
  // READY:
  // Kitchen has prepared the order.
  //
  // OUT_FOR_DELIVERY:
  // Kitchen clicked "Out for Delivery".
  //
  // DINE-IN:
  // NEVER appears here.
  //
  // We intentionally check STATUS first.
  // Therefore even if backend uses a slightly different
  // order_type value, an order with
  // status = out_for_delivery WILL appear.
  // =========================================================

  const readyOrders = orders.filter(
    (order) =>
      order.status === "ready" &&
      order.order_type !== "DINE_IN"
  );

  const outForDeliveryOrders =
    orders.filter(
      (order) =>
        order.status ===
        "out_for_delivery"
    );

  const deliveredOrders =
    orders.filter(
      (order) =>
        order.status === "delivered"
    );

  // =========================================================
  // ORDERS SHOWN ON DELIVERY PANEL
  // =========================================================

  const deliveryOrders = [
    ...readyOrders,
    ...outForDeliveryOrders,
  ];

  // =========================================================
  // PANEL
  // =========================================================

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">

        <div>

          <div className="text-xs uppercase tracking-widest text-[#B8E14A]">
            Delivery
          </div>

          <h1 className="font-heading text-4xl">
            Delivery Orders
          </h1>

          <p className="text-sm text-[#B7D8D5] mt-2">
            Pick up ready orders and deliver
            them to customers.
          </p>

        </div>

        <button
          data-testid="delivery-logout"
          onClick={logout}
          className="btn-outline text-sm"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>

      </div>

      {/* =====================================================
          STATS
      ===================================================== */}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">

        <Stat
          icon={Package}
          label="Ready"
          value={readyOrders.length}
        />

        <Stat
          icon={Bike}
          label="Out for Delivery"
          value={
            outForDeliveryOrders.length
          }
        />

        <Stat
          icon={CheckCircle2}
          label="Delivered"
          value={
            deliveredOrders.length
          }
        />

      </div>

      {/* =====================================================
          DELIVERY ORDERS
      ===================================================== */}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">

        {deliveryOrders.map(
          (order) => {

            const delivery =
              order.delivery || {};

            const destinationLat =
              Number(
                  delivery.lat ??
                  delivery.latitude
              );

            const destinationLng =
              Number(
                  delivery.lng ??
                  delivery.longitude
              );

            const liveLat =
              Number(
                order.delivery_lat ??
                  order.current_lat ??
                  order.delivery_partner_lat ??
                  order.driver_lat
              );

            const liveLng =
              Number(
                order.delivery_lng ??
                  order.current_lng ??
                  order.delivery_partner_lng ??
                  order.driver_lng
              );

            const hasDestination =
              Number.isFinite(
                destinationLat
              ) &&
              Number.isFinite(
                destinationLng
              );

            const hasLiveLocation =
              Number.isFinite(
                liveLat
              ) &&
              Number.isFinite(
                liveLng
              );

            const isOutForDelivery =
              order.status ===
              "out_for_delivery";

            return (
              <motion.div
                key={order.id}
                initial={{
                  opacity: 0,
                  y: 10,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                className="bg-white text-[#1F5F5C] rounded-3xl p-5 shadow-lg"
              >

                {/* =================================================
                    ORDER HEADER
                ================================================= */}

                <div className="flex items-center justify-between mb-4">

                  <div>

                    <div className="font-heading text-xl">
                      {order.is_catering
                        ? `Catering - ${order.event_type || "Event"}`
                        : order.order_code}
                    </div>

                    {order.is_catering && (
                      <div className="text-xs text-[#B8E14A] font-semibold mt-1">
                        CATERING DELIVERY
                      </div>
                    )}

                    <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3" />
                      {timeAgo(
                        order.created_at
                      )}
                    </div>

                  </div>

                  <div
                    className={`text-xs px-3 py-1 rounded-full ${
                      order.status ===
                      "ready"
                        ? "bg-green-100 text-green-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {order.status.replaceAll(
                      "_",
                      " "
                    )}
                  </div>

                </div>

                {/* =================================================
                    CUSTOMER
                ================================================= */}

                <div className="border-t border-gray-200 pt-4">

                  <div className="font-semibold text-lg">
                    {delivery.name ||
                      "Customer"}
                  </div>

                  {delivery.phone && (
                    <a
                      href={`tel:${delivery.phone}`}
                      className="flex items-center gap-2 text-sm text-blue-600 mt-2"
                    >
                      <Phone className="w-4 h-4" />
                      {delivery.phone}
                    </a>
                  )}

                </div>
                {order.is_catering && (
                  <div className="mt-3 rounded-xl bg-[#B8E14A]/10 p-3 text-sm">
                    <div>
                      <strong>Event:</strong>{" "}
                      {order.event_type || "Catering Event"}
                    </div>

                    <div className="mt-1">
                      <strong>Date:</strong>{" "}
                      {order.event_date || "Not provided"}
                    </div>

                    <div className="mt-1">
                      <strong>Guests:</strong>{" "}
                      {order.guests || 0}
                    </div>

                    {order.requirements && (
                      <div className="mt-1">
                        <strong>Requirements:</strong>{" "}
                        {order.requirements}
                      </div>
                    )}
                  </div>
                )}
                {/* =================================================
                    DESTINATION
                ================================================= */}

                <div className="mt-4 text-sm">

                  <div className="flex items-start gap-2">

                    <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#B8E14A]" />

                    <div>

                      <div>
                        {delivery.address ||
                          "Address not provided"}
                      </div>

                      {delivery.landmark && (
                        <div className="text-gray-500 mt-1">
                          Landmark:{" "}
                          {delivery.landmark}
                        </div>
                      )}

                      {delivery.pincode && (
                        <div className="text-gray-500 mt-1">
                          Pincode:{" "}
                          {delivery.pincode}
                        </div>
                      )}

                    </div>

                  </div>

                </div>

                {/* =================================================
                    ITEMS
                ================================================= */}

                {!order.is_catering && (
                  <div className="mt-4 border-t border-gray-200 pt-4">

                    <div className="font-semibold text-sm mb-2">
                      Order Items
                    </div>

                    <div className="space-y-1 text-sm">
                      {Array.isArray(order.items) &&
                        order.items.map((item, index) => (
                          <div
                            key={index}
                            className="flex justify-between gap-2"
                          >
                            <span>
                              {item.name} × {item.quantity}
                            </span>

                            <span>
                              ₹
                              {Number(item.price || 0) *
                                Number(item.quantity || 0)}
                            </span>
                          </div>
                        ))}
                    </div>

                  </div>
                )}

                {/* =================================================
                    TOTAL
                ================================================= */}

                <div className="border-t border-gray-200 mt-4 pt-4 flex justify-between font-semibold">

                  <span>
                    Total
                  </span>

                  <span className="text-[#B8E14A]">
                    ₹{order.total}
                  </span>

                </div>

                {/* =================================================
                    BUTTONS
                ================================================= */}

                <div className="flex flex-wrap gap-2 mt-5">

                  {/* ---------------------------------------------
                      READY → PICK UP
                  --------------------------------------------- */}

                  {order.status ===
                    "ready" && (
                    <button
                      onClick={() =>
                        setStatus(
                          order.id,
                          "out_for_delivery"
                        )
                      }
                      className="w-full text-xs px-3 py-2 rounded-full bg-[#B8E14A] text-[#1F5F5C] font-semibold"
                    >
                      <Bike className="inline w-4 h-4 mr-1" />

                      Pick Up Order
                    </button>
                  )}

                  {/* ---------------------------------------------
                      OPEN CUSTOMER MAP
                  --------------------------------------------- */}

                  <button
                    onClick={() =>
                      openCustomerLocation(
                        order
                      )
                    }
                    className="flex-1 text-xs px-3 py-2 rounded-full bg-blue-600 text-white font-semibold"
                  >
                    <Navigation className="inline w-4 h-4 mr-1" />

                    Open GPS
                  </button>

                  {/* ---------------------------------------------
                      LIVE GPS
                  --------------------------------------------- */}

                  {isOutForDelivery && (
                    <>
                      <button
                        onClick={() =>
                          startLiveTracking(
                            order.id
                          )
                        }
                        className="w-full text-xs px-3 py-2 rounded-full bg-[#1F5F5C] text-white font-semibold"
                      >
                        📍 Start Live GPS
                      </button>

                      <button
                        onClick={() =>
                          stopLiveTracking(
                            order.id
                          )
                        }
                        className="w-full text-xs px-3 py-2 rounded-full border border-gray-300"
                      >
                        Stop GPS
                      </button>

                      {/* -----------------------------------------
                          MARK DELIVERED
                      ----------------------------------------- */}

                      <button
                        onClick={() =>
                          setStatus(
                            order.id,
                            "delivered"
                          )
                        }
                        className="w-full text-xs px-3 py-2 rounded-full bg-green-700 text-white font-semibold"
                      >
                        <CheckCircle2 className="inline w-4 h-4 mr-1" />

                        Mark Delivered
                      </button>
                    </>
                  )}

                </div>
                
                {/* =================================================
                    MAP
                    ONLY APPEARS INSIDE AN ORDER CARD
                    NOT AS THE WHOLE DELIVERY PAGE
                ================================================= */}

                {isOutForDelivery &&
                  (hasDestination ||
                    hasLiveLocation) && (
                    <div className="mt-4">

                      <div className="text-xs uppercase tracking-widest text-[#1F5F5C] mb-2">
                        Delivery Location
                      </div>

                      <DeliveryMap
                        lat={
                          hasLiveLocation
                            ? liveLat
                            : null
                        }
                        lng={
                          hasLiveLocation
                            ? liveLng
                            : null
                        }
                        destinationLat={
                          hasDestination
                            ? destinationLat
                            : null
                        }
                        destinationLng={
                          hasDestination
                            ? destinationLng
                            : null
                        }
                      />

                    </div>
                  )}

              </motion.div>
            );
          }
        )}

      </div>

      {/* =====================================================
          NO ORDERS
      ===================================================== */}

      {deliveryOrders.length ===
        0 && (
        <div className="glass rounded-3xl p-12 text-center">

          <Bike className="w-12 h-12 mx-auto text-[#B8E14A] mb-4" />

          <h2 className="font-heading text-2xl">
            No delivery orders
          </h2>

          <p className="text-sm text-[#B7D8D5] mt-2">
            Orders will appear here when
            they are ready for delivery.
          </p>

        </div>
      )}

    </div>
  );
}

// =========================================================
// STAT COMPONENT
// =========================================================

function Stat({
  icon: Icon,
  label,
  value,
}) {
  return (
    <div className="glass rounded-3xl p-5 flex items-center gap-4">

      <div className="bg-[#B8E14A]/20 rounded-2xl p-3">

        <Icon className="w-6 h-6 text-[#B8E14A]" />

      </div>

      <div>

        <div className="font-heading text-2xl">
          {value}
        </div>

        <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
          {label}
        </div>

      </div>

    </div>
  );
}

// =========================================================
// TIME AGO
// =========================================================

function timeAgo(iso) {
  if (!iso) {
    return "—";
  }

  const seconds = Math.floor(
    (Date.now() -
      new Date(iso).getTime()) /
      1000
  );

  if (!Number.isFinite(seconds)) {
    return "—";
  }

  if (seconds < 60) {
    return `${Math.max(
      0,
      seconds
    )}s ago`;
  }

  if (seconds < 3600) {
    return `${Math.floor(
      seconds / 60
    )}m ago`;
  }

  return `${Math.floor(
    seconds / 3600
  )}h ago`;
}